import React, { useRef, useState } from 'react';

const imageAlignOptions = [
  { label: 'Left', value: 'left' },
  { label: 'Center', value: 'center' },
  { label: 'Right', value: 'right' },
];

const imageWidthOptions = [
  { label: 'Full', value: '100%' },
  { label: 'Wide', value: '75%' },
  { label: 'Half', value: '50%' },
  { label: 'Small', value: '320px' },
];

const insertAtSelection = (textarea, value, insertion) => {
  const start = textarea?.selectionStart ?? value.length;
  const end = textarea?.selectionEnd ?? value.length;
  return `${value.slice(0, start)}${insertion}${value.slice(end)}`;
};

const imageMarkup = ({ align, alt, url, width }) => (
  `<img src="${url}" alt="${alt || ''}" data-align="${align}" data-width="${width}" />`
);

function RichContentEditor({
  className = '',
  id,
  label = 'Rich content',
  onChange,
  placeholder,
  uploadImage,
  value = '',
}) {
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const [imageAlign, setImageAlign] = useState('center');
  const [imageWidth, setImageWidth] = useState('100%');
  const [imageAlt, setImageAlt] = useState('');
  const [status, setStatus] = useState('');

  const insertImage = async (file) => {
    if (!file?.type?.startsWith('image/')) return;

    setStatus('Uploading image...');
    try {
      const url = await uploadImage?.(file);
      if (!url) throw new Error('Image upload is not available.');

      const insertion = `\n\n${imageMarkup({
        align: imageAlign,
        alt: imageAlt || file.name.replace(/\.[^/.]+$/, ''),
        url,
        width: imageWidth,
      })}\n\n`;

      const nextValue = insertAtSelection(textareaRef.current, value, insertion);
      onChange(nextValue);
      setStatus('Image inserted.');
      window.requestAnimationFrame(() => textareaRef.current?.focus());
    } catch (error) {
      setStatus(error.message || 'Unable to insert image.');
    }
  };

  const handlePaste = async (event) => {
    const imageFile = Array.from(event.clipboardData?.files || [])
      .find((file) => file.type.startsWith('image/'));
    if (!imageFile) return;

    event.preventDefault();
    await insertImage(imageFile);
  };

  return (
    <div className={className}>
      <div className="rich-content-editor-toolbar">
        <label className="field-label mt-0" htmlFor={id}>{label}</label>
        <div className="rich-content-editor-controls">
          <input
            aria-label="Image alt text"
            className="field rich-content-editor-alt"
            onChange={(event) => setImageAlt(event.target.value)}
            placeholder="Image alt"
            value={imageAlt}
          />
          <select
            aria-label="Image alignment"
            className="field rich-content-editor-select"
            onChange={(event) => setImageAlign(event.target.value)}
            value={imageAlign}
          >
            {imageAlignOptions.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
          <select
            aria-label="Image width"
            className="field rich-content-editor-select"
            onChange={(event) => setImageWidth(event.target.value)}
            value={imageWidth}
          >
            {imageWidthOptions.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
          <button
            className="rounded border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 transition hover:border-slate-950 hover:text-slate-950 disabled:cursor-not-allowed disabled:opacity-50"
            disabled={!uploadImage}
            onClick={() => fileInputRef.current?.click()}
            type="button"
          >
            Upload image
          </button>
          <input
            ref={fileInputRef}
            accept="image/*"
            className="hidden"
            onChange={(event) => {
              const file = event.target.files?.[0];
              event.target.value = '';
              insertImage(file);
            }}
            type="file"
          />
        </div>
      </div>
      <textarea
        ref={textareaRef}
        id={id}
        className="field min-h-80 font-mono text-xs"
        onChange={(event) => onChange(event.target.value)}
        onPaste={handlePaste}
        placeholder={placeholder}
        value={value}
      />
      {status && <p className="mt-2 text-xs font-semibold text-slate-500">{status}</p>}
    </div>
  );
}

export default RichContentEditor;
